/**
 * Maps the user's pre-AINA pathname (the value returned by
 * `peekAinaReturnPath`) to a list of breadcrumb crumbs that should appear
 * BEFORE the "AINA - AI Mentor" segment on AINA-spawned sub-pages
 * (e.g. the AINA doubt chat at `/doubts/aina-chat`).
 *
 * Kept intentionally narrow (one leading segment) for the first iteration —
 * the table can be extended to handle nested sources like
 * `Tests / Infinity Learn Test Series / AINA` without changing call sites.
 */

/**
 * Route segments are duplicated here intentionally rather than importing
 * `ROUTESWEB` from `../index` (which would create a circular import within
 * `@repo/utils`). Keep these in sync with `packages/utils/src/index.ts`.
 */
const ROUTE_DASHBOARD = "dashboard";
const ROUTE_TESTS = "tests";
const ROUTE_LIVE_CLASSES = "live-classes";
const ROUTE_COURSES = "courses";
const ROUTE_COURSE_OVERVIEW = "mycourse";
const ROUTE_SELF_LEARN = "self-learn";

export type AinaSourceCrumb = {
  /** Stable React `key` and lookup id. */
  key: string;
  /** i18n key inside the `doubts` namespace. */
  labelKey: string;
  /** Where clicking the crumb should navigate. */
  href: string;
};

const HOME_CRUMB: AinaSourceCrumb = {
  key: "home",
  labelKey: "aina_breadcrumb_home",
  href: `/${ROUTE_DASHBOARD}`,
};

const TESTS_CRUMB: AinaSourceCrumb = {
  key: "tests",
  labelKey: "aina_breadcrumb_tests",
  href: `/${ROUTE_TESTS}`,
};

const LIVE_CLASSES_CRUMB: AinaSourceCrumb = {
  key: "liveClasses",
  labelKey: "aina_breadcrumb_live_classes",
  href: `/${ROUTE_LIVE_CLASSES}`,
};

const SELF_LEARN_CRUMB: AinaSourceCrumb = {
  key: "selfLearn",
  labelKey: "aina_breadcrumb_self_learn",
  href: `/${ROUTE_SELF_LEARN}`,
};

function firstPathSegment(returnPath: string | null): string | null {
  if (!returnPath) return null;
  const segments = returnPath.replace(/^\//, "").split("/").filter(Boolean);
  return segments[0] ?? null;
}

export function resolveAinaSourceBreadcrumb(
  returnPath: string | null,
): AinaSourceCrumb[] {
  const first = firstPathSegment(returnPath);
  switch (first) {
    case ROUTE_TESTS:
      return [TESTS_CRUMB];
    case ROUTE_LIVE_CLASSES:
    case ROUTE_COURSES:
    case ROUTE_COURSE_OVERVIEW:
      return [LIVE_CLASSES_CRUMB];
    case ROUTE_SELF_LEARN:
      return [SELF_LEARN_CRUMB];
    case ROUTE_DASHBOARD:
    default:
      return [HOME_CRUMB];
  }
}
